import apiClient from './apiService';

const BASE = '/api/v1/gov';

export interface Population {
  id: number;
  entity_id: number;
  population_name: string;
  description?: string | null;
  is_active: number;
}

/** Minimal SOP (gov process) shape for entity-wide pickers. */
export interface SopLite {
  id: number;
  process_name: string;
  process_code?: string | null;
  entity_id?: number;
  is_active?: number;
}

export interface MySop {
  id: number;
  process_code?: string | null;
  process_name: string;
  description?: string | null;
  process_status: string;
  version?: string | null;
  effective_date?: string | null;
  entity_id: number;
  is_active: number;
  step_count: number;
}

export interface ProcessInstance {
  id: number;
  entity_id: number;
  process_id: number;
  process_name?: string | null;
  process_code?: string | null;
  location_id?: number | null;
  location_name?: string | null;
  geography_override: boolean;
  population_id?: number | null;
  population_name?: string | null;
  transaction_type: string;
  sample_text?: string | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  initiated_by: number;
  initiated_by_name?: string | null;
  initiated_on?: string | null;
  step_count: number;
  completed_count: number;
  created_on?: string | null;
}

export interface AuditExecution {
  id: number;
  instance_step_id: number;
  audit_link_id: number;
  procedure_template_id: number;
  procedure_name?: string | null;
  procedure_type?: string | null;
  procedure_instance_id?: number | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  submitted_by?: number | null;
  submitted_on?: string | null;
}

export interface InstanceStep {
  id: number;
  instance_id: number;
  process_step_id: number;
  step_number: number;
  step_name?: string | null;
  authority_id: number;
  authority_name?: string | null;
  role: string;
  status: 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'SKIPPED';
  completed_by?: number | null;
  completed_on?: string | null;
  notes?: string | null;
  audit_executions: AuditExecution[];
}

export interface ProcessInstanceDetail extends ProcessInstance {
  steps: InstanceStep[];
}

export interface InitiationData {
  default_location_id?: number | null;
  default_location_name?: string | null;
  my_location_ids: number[];
  populations: Population[];
  step_count: number;
}

export interface LocationLite {
  id: number;
  name: string;
}

export const TRANSACTION_TYPES = ['Monthly', 'Quarterly', 'Annual', 'Ad-hoc'] as const;
export type TransactionType = typeof TRANSACTION_TYPES[number];

export const ROLE_LABEL: Record<string, string> = {
  PERFORM: 'Performs',
  APPROVE: 'Approves',
  REVIEW: 'Reviews',
  NOTIFY: 'Is notified',
  OTHER: 'Other',
};

export const STEP_STATUS_COLOR: Record<string, string> = {
  PENDING:   'bg-gray-100 text-gray-500',
  ACTIVE:    'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-green-100 text-green-700',
  SKIPPED:   'bg-yellow-100 text-yellow-700',
};

export const INSTANCE_STATUS_COLOR: Record<string, string> = {
  PENDING:     'bg-gray-100 text-gray-500',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETED:   'bg-green-100 text-green-700',
  CANCELLED:   'bg-red-100 text-red-600',
};

const sopInstanceService = {
  async listMySops(): Promise<MySop[]> {
    const r = await apiClient.get(`${BASE}/my-sops`);
    return Array.isArray(r.data) ? r.data : r.data?.data ?? [];
  },

  /** Approved + active SOPs (gov processes) under the current entity — for the
      RCA Process identifier picker. Uses the unified /dropdown endpoint which
      filters process_status='APPROVED' AND is_active=1 server-side, so
      draft/unapproved SOPs never appear. Body = { items:[{id,name,code?}] }. */
  async listAllSops(): Promise<SopLite[]> {
    const r = await apiClient.get(`${BASE}/processes/dropdown`);
    const items = (r.data?.items ?? []) as { id: number; name: string; code?: string | null }[];
    return items.map(i => ({ id: i.id, process_name: i.name, process_code: i.code ?? null }));
  },

  /** Authority ids the logged-in person holds in this entity. A step is "mine"
      when its authority is in this set (a person may hold several authorities). */
  async getMyAuthorities(): Promise<number[]> {
    const r = await apiClient.get(`${BASE}/my-authorities`);
    const arr = Array.isArray(r.data) ? r.data : r.data?.data ?? [];
    return arr.map((a: { authority_id: number }) => a.authority_id);
  },

  async getInitiationData(processId: number): Promise<InitiationData> {
    const r = await apiClient.get(`${BASE}/processes/${processId}/initiation-data`);
    return r.data;
  },

  async listEntityLocations(): Promise<LocationLite[]> {
    const r = await apiClient.get(`${BASE}/entity-locations`);
    return Array.isArray(r.data) ? r.data : r.data?.data ?? [];
  },

  async listPopulations(): Promise<Population[]> {
    const r = await apiClient.get(`${BASE}/populations`);
    return Array.isArray(r.data) ? r.data : r.data?.data ?? [];
  },

  async createInstance(payload: {
    process_id: number;
    location_id?: number | null;
    geography_override?: boolean;
    population_id?: number | null;
    transaction_type: string;
    sample_text?: string | null;
  }): Promise<ProcessInstance> {
    const r = await apiClient.post(`${BASE}/process-instances`, payload);
    return r.data;
  },

  async listInstances(view: 'my_initiated' | 'my_tasks' = 'my_tasks'): Promise<ProcessInstance[]> {
    const r = await apiClient.get(`${BASE}/process-instances`, { params: { view } });
    return Array.isArray(r.data) ? r.data : r.data?.data ?? [];
  },

  async getInstance(instanceId: number): Promise<ProcessInstanceDetail> {
    const r = await apiClient.get(`${BASE}/process-instances/${instanceId}`);
    return r.data;
  },

  async cancelInstance(instanceId: number): Promise<void> {
    await apiClient.post(`${BASE}/process-instances/${instanceId}/cancel`);
  },

  async completeStep(stepId: number, notes?: string): Promise<void> {
    await apiClient.post(`${BASE}/process-instance-steps/${stepId}/complete`, { notes: notes ?? null });
  },

  async skipStep(stepId: number): Promise<void> {
    await apiClient.post(`${BASE}/process-instance-steps/${stepId}/skip`);
  },

  async startAuditExecution(executionId: number): Promise<{ procedure_instance_id: number; procedure_type: string; already_started: boolean }> {
    const r = await apiClient.post(`${BASE}/instance-audit-executions/${executionId}/start`);
    return r.data;
  },

  async completeAuditExecution(executionId: number): Promise<void> {
    await apiClient.post(`${BASE}/instance-audit-executions/${executionId}/complete`);
  },
};

export default sopInstanceService;
